import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search, RefreshCw, FileText, Eye, Download, ChevronUp, ChevronDown,
  CheckCircle, XCircle, Clock, AlertCircle, Calendar, X,
  Printer, Mail, Percent, Copy, AlertTriangle, Send, Repeat,
  Settings, Check, FileSpreadsheet, Trash2, GripVertical, ArrowUpDown, RotateCcw
} from 'lucide-react';

// Configuración de columnas disponibles.
// align: alineación de encabezado y celda. secondary: columna auxiliar (letra más pequeña y gris).
const COLUMN_CONFIG = {
  tipo: { label: 'Tipo', default: true, fixed: true, align: 'left' },
  consecutivo: { label: 'Consecutivo Electrónico No.', shortLabel: 'Consecutivo', default: true, fixed: true, align: 'left' },
  fecha: { label: 'Fecha', default: true, fixed: true, align: 'left' },
  cliente: { label: 'Cliente', default: true, fixed: true, align: 'left' },
  divisa: { label: 'Divisa', default: true, align: 'center' },
  termino: { label: 'Término', default: true, align: 'center' },
  referencia: { label: 'Referencia', default: true, align: 'left' },
  subtotal: { label: 'SubTotal', default: true, fixed: true, align: 'right' },
  impuestos: { label: 'Impuestos', default: true, align: 'right' },
  total: { label: 'Total', default: true, fixed: true, align: 'right' },
  tc: { label: 'TC', default: false, align: 'center', secondary: true },
  baseCRC: { label: 'Base CRC', default: false, align: 'right', secondary: true },
  ivaCRC: { label: 'IVA CRC', default: false, align: 'right', secondary: true },
  tarifaIva: { label: '% IVA', default: false, align: 'center', secondary: true },
  t911CRC: { label: '911 CRC', default: false, align: 'right', secondary: true },
  cruzRojaCRC: { label: 'C.Roja CRC', default: false, align: 'right', secondary: true },
  cabys: { label: 'CABYS', default: false, align: 'left', secondary: true },
  estado: { label: 'Estado Hacienda', shortLabel: 'Estado', default: true, fixed: true, align: 'center' }
};

const DEFAULT_COLUMN_ORDER = Object.keys(COLUMN_CONFIG);
const ALIGN_CLASS = { left: 'text-left', center: 'text-center', right: 'text-right' };

// Obtener columnas visibles de localStorage o usar defaults
const getDefaultColumns = () => {
  const defaults = Object.fromEntries(
    Object.entries(COLUMN_CONFIG).map(([key, config]) => [key, config.default])
  );
  try {
    const saved = localStorage.getItem('historialFacturas_columns');
    return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
  } catch {
    return defaults;
  }
};

// Orden de columnas guardado. Se descartan columnas que ya no existen y las
// nuevas (que no estaban cuando se guardó) se agregan al final.
const getColumnOrder = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('historialFacturas_columnOrder') || 'null');
    if (Array.isArray(saved)) {
      const valid = saved.filter(k => COLUMN_CONFIG[k]);
      return [...valid, ...DEFAULT_COLUMN_ORDER.filter(k => !valid.includes(k))];
    }
  } catch { /* orden por defecto */ }
  return DEFAULT_COLUMN_ORDER;
};

import { documentsApi, recurringApi } from '../../utils/api';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import PrintPreviewModal from '../../components/PrintPreviewModal';
import DocumentDetailModal from '../../components/DocumentDetailModal';
import RecurrenteConfigModal from '../../components/RecurrenteConfigModal';
import AnulacionNCPreviewModal from '../../components/AnulacionNCPreviewModal';
import MonthRangePicker from '../../components/MonthRangePicker';
import { copiarLineasParaAnular } from '../../utils/anulacion';
import useAuthStore from '../../store/authStore';

import useFiltroPeriodo from '../../hooks/useFiltroPeriodo';
// Date range options
const DATE_RANGES = {
  'hoy': { label: 'Hoy', getDates: () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return { desde: today, hasta: new Date() };
  }},
  'ayer': { label: 'Ayer', getDates: () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    const yesterdayEnd = new Date(yesterday);
    yesterdayEnd.setHours(23, 59, 59, 999);
    return { desde: yesterday, hasta: yesterdayEnd };
  }},
  'esta_semana': { label: 'Esta semana', getDates: () => {
    const today = new Date();
    const firstDay = new Date(today);
    firstDay.setDate(today.getDate() - today.getDay());
    firstDay.setHours(0, 0, 0, 0);
    return { desde: firstDay, hasta: new Date() };
  }},
  'semana_pasada': { label: 'Semana pasada', getDates: () => {
    const today = new Date();
    const lastWeekEnd = new Date(today);
    lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
    lastWeekEnd.setHours(23, 59, 59, 999);
    const lastWeekStart = new Date(lastWeekEnd);
    lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
    lastWeekStart.setHours(0, 0, 0, 0);
    return { desde: lastWeekStart, hasta: lastWeekEnd };
  }},
  'este_mes': { label: 'Este mes', getDates: () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return { desde: firstDay, hasta: new Date() };
  }},
  'mes_anterior': { label: 'Mes anterior', getDates: () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
    lastDay.setHours(23, 59, 59, 999);
    return { desde: firstDay, hasta: lastDay };
  }},
  'este_ano': { label: 'Este año', getDates: () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), 0, 1);
    return { desde: firstDay, hasta: new Date() };
  }},
  'ano_anterior': { label: 'Año anterior', getDates: () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear() - 1, 0, 1);
    const lastDay = new Date(today.getFullYear() - 1, 11, 31);
    lastDay.setHours(23, 59, 59, 999);
    return { desde: firstDay, hasta: lastDay };
  }},
  'ultimos_3_meses': { label: 'Últimos 3 meses', getDates: () => {
    const today = new Date();
    const firstDay = new Date(today);
    firstDay.setMonth(firstDay.getMonth() - 3);
    firstDay.setHours(0, 0, 0, 0);
    return { desde: firstDay, hasta: new Date() };
  }},
  'ultimos_6_meses': { label: 'Últimos 6 meses', getDates: () => {
    const today = new Date();
    const firstDay = new Date(today);
    firstDay.setMonth(firstDay.getMonth() - 6);
    firstDay.setHours(0, 0, 0, 0);
    return { desde: firstDay, hasta: new Date() };
  }},
  'ultimos_12_meses': { label: 'Últimos 12 meses', getDates: () => {
    const today = new Date();
    const firstDay = new Date(today);
    firstDay.setMonth(firstDay.getMonth() - 12);
    firstDay.setHours(0, 0, 0, 0);
    return { desde: firstDay, hasta: new Date() };
  }},
  'todo': { label: 'Todo el tiempo', getDates: () => ({ desde: null, hasta: null }) },
  'personalizado': { label: 'Personalizado', getDates: () => null }
};

// Solo estados de documentos enviados a Hacienda (sin borradores)
const STATUS_CONFIG = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  processing: { label: 'Procesando', color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
  accepted: { label: 'Aceptada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-700', icon: XCircle },
  error: { label: 'Error', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  cancelled: { label: 'Descartado', color: 'bg-gray-200 text-gray-500', icon: XCircle },
};

const TIPO_DOC = {
  '01': 'FE',
  '02': 'ND',
  '03': 'NC',
  '04': 'TE',
};

const TIPO_DOC_FULL = {
  '01': 'Factura Electrónica',
  '02': 'Nota de Débito',
  '03': 'Nota de Crédito',
  '04': 'Tiquete Electrónico',
};

export default function HistorialFacturas() {
  const navigate = useNavigate();
  const { currentEmisor } = useAuthStore();
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  // Filtro de período unificado (MonthRangePicker). Default = mes actual.
  const [filtroFecha, setFiltroFecha] = useFiltroPeriodo('HistorialFacturas', () => {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, n.getMonth() + 1, 0).getDate();
    return {
      modo: 'mes',
      periodo: `${y}-${m}`,
      desde: `${y}-${m}-01`,
      hasta: `${y}-${m}-${String(last).padStart(2, '0')}`
    };
  });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [sortField, setSortField] = useState('consecutivo');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(getDefaultColumns);
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    if (COLUMN_CONFIG[columnKey]?.fixed) return; // No toggle fixed columns

    setVisibleColumns(prev => {
      const updated = { ...prev, [columnKey]: !prev[columnKey] };
      try { localStorage.setItem('historialFacturas_columns', JSON.stringify(updated)); } catch { /* sin storage */ }
      return updated;
    });
  };

  // Orden de columnas (arrastrar encabezados)
  const [columnOrder, setColumnOrder] = useState(getColumnOrder);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const orderedColumns = columnOrder.filter(key => visibleColumns[key]);

  const saveColumnOrder = (order) => {
    setColumnOrder(order);
    try { localStorage.setItem('historialFacturas_columnOrder', JSON.stringify(order)); } catch { /* sin storage */ }
  };

  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === columnKey) return;
    const next = columnOrder.filter(k => k !== draggedColumn);
    next.splice(next.indexOf(columnKey), 0, draggedColumn);
    saveColumnOrder(next);
  };

  const handleDragEnd = () => setDraggedColumn(null);

  const resetColumns = () => {
    saveColumnOrder(DEFAULT_COLUMN_ORDER);
    const defaults = Object.fromEntries(
      Object.entries(COLUMN_CONFIG).map(([key, config]) => [key, config.default])
    );
    setVisibleColumns(defaults);
    try { localStorage.setItem('historialFacturas_columns', JSON.stringify(defaults)); } catch { /* sin storage */ }
  };
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // NC Modal state
  const [showNCModal, setShowNCModal] = useState(false);
  const [ncMotivo, setNCMotivo] = useState('anulacion');
  const [ncRazon, setNCRazon] = useState('');
  const [creatingNC, setCreatingNC] = useState(false);

  // Recurring Modal state — usa el componente compartido RecurrenteConfigModal
  // que llama al endpoint createFromDocumento (copia líneas con exoneración y JSONs completos).
  const [showRecurringModal, setShowRecurringModal] = useState(false);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Delete cancelled doc state
  const [canDeleteSelected, setCanDeleteSelected] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Modal mensaje Hacienda (para ver errores de rechazo)
  const [mensajeHaciendaModal, setMensajeHaciendaModal] = useState(null);

  // Modal cancelar documento
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingDoc, setCancellingDoc] = useState(false);

  // Modal confirmar envío email
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Check if selected cancelled doc can be deleted.
  // Regla: cualquier documento en estado 'cancelled' es eliminable — Hacienda
  // no lo aceptó (o el usuario lo anuló) así que borrarlo no rompe el consecutivo.
  // Antes se exigía un reemplazo aceptado, pero eso dejaba huérfanos los docs
  // rechazados sin retry posterior (bug visto 2026-07-03 con FE 4132).
  useEffect(() => {
    setCanDeleteSelected(!!selectedDoc && selectedDoc.status === 'cancelled');
  }, [selectedDoc]);

  const cargarDocumentos = async (page = 1) => {
    setLoading(true);
    try {
      // excludeDraft=true: Solo mostrar documentos enviados a Hacienda.
      // limit alto para que se cargue todo en una sola página — la navegación se hace
      // por scroll interno de la tabla, no por paginación.
      const params = { page, limit: 10000, excludeDraft: true, ligero: '1' }; // sin XML: el detalle los trae aparte
      if (search) params.search = search;
      if (filtroStatus) params.status = filtroStatus;
      if (filtroTipo) params.tipo = filtroTipo;

      // Helper to format date as local YYYY-MM-DD (avoids timezone issues)
      const formatLocalDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Filtro de fecha unificado (MonthRangePicker → { desde, hasta })
      if (filtroFecha?.desde) params.desde = filtroFecha.desde;
      if (filtroFecha?.hasta) params.hasta = filtroFecha.hasta;

      // NC y ND tienen su propia pantalla: el Historial es de facturas y tiquetes
      params.excludeTipos = '02,03,COT';
      const response = await documentsApi.list(params);
      setDocumentos(response.data.data || []);
      setPagination(response.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (error) {
      console.error('Error cargando documentos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search for live filtering
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    cargarDocumentos();
  }, [filtroStatus, filtroTipo, filtroFecha, debouncedSearch]);

  // Export functions
  const getExportParams = () => {
    const params = {};
    if (search) params.search = search;
    if (filtroStatus) params.status = filtroStatus;
    if (filtroTipo) params.tipo = filtroTipo;

    if (filtroFecha?.desde) params.desde = filtroFecha.desde;
    if (filtroFecha?.hasta) params.hasta = filtroFecha.hasta;

    // Include visible columns for export
    // Mismo orden que en pantalla
    params.columns = orderedColumns.join(',');

    // Pass current sort
    if (sortField) params.sortField = sortField;
    if (sortDirection) params.sortDir = sortDirection;

    return params;
  };

  // Nombre de archivo: <emisor>_Historico_Facturacion_<mes o rango>_<año>
  const buildExportFilename = (ext) => {
    const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const desde = filtroFecha?.desde;
    const hasta = filtroFecha?.hasta;
    let periodo = 'Todos';
    if (desde && hasta) {
      const [dy, dm] = desde.split('-');
      const [hy, hm] = hasta.split('-');
      if (dy === hy && dm === hm) periodo = `${MESES[parseInt(dm, 10) - 1]}_${dy}`;
      else periodo = `${desde}_a_${hasta}`;
    } else if (desde) periodo = `desde_${desde}`;
    else if (hasta) periodo = `hasta_${hasta}`;
    const slug = (currentEmisor?.nombre || 'Emisor').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return `${slug}_Historico_Facturacion_${periodo}.${ext}`;
  };

  const exportarExcel = async () => {
    setExporting(true);
    try {
      const response = await documentsApi.exportExcel(getExportParams());
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildExportFilename('xlsx');
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Excel exportado');
    } catch (err) {
      console.error('Error exportando Excel:', err);
      toast.error('Error al exportar Excel');
    } finally {
      setExporting(false);
    }
  };

  const exportarPdf = async () => {
    setExporting(true);
    try {
      const response = await documentsApi.exportPdf(getExportParams());
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildExportFilename('pdf');
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF exportado');
    } catch (err) {
      console.error('Error exportando PDF:', err);
      toast.error('Error al exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  // Auto-refresh every 30 seconds (only when there are documents in "processing" state)
  const hasProcessingDocs = documentos.some(d => d.status === 'processing');

  useEffect(() => {
    if (!autoRefresh || !hasProcessingDocs) return;

    const interval = setInterval(() => {
      cargarDocumentos(pagination.page);
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, hasProcessingDocs, pagination.page, filtroStatus, filtroTipo, filtroFecha]);

  const handleSearch = (e) => {
    e.preventDefault();
    cargarDocumentos(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      // Texto arranca A→Z; números y fechas arrancan del mayor/más reciente
      setSortDirection(['cliente', 'tipo', 'divisa', 'termino', 'referencia', 'cabys', 'estado'].includes(field) ? 'asc' : 'desc');
    }
  };

  // Valor por el que se ordena cada columna. Los montos se comparan en colones
  // (monto × TC) para que una factura en USD no quede por debajo de una en CRC
  // más chica. Las NC cuentan en negativo, igual que se muestran.
  const getSortValue = (doc, field) => {
    const tipoDoc = doc.tipo_documento || doc.tipoDocumento || '01';
    const signo = tipoDoc === '03' ? -1 : 1;
    const tc = parseFloat(doc.tipoCambio) || 1;
    const subtotal = parseFloat(doc.total_venta_neta || doc.totales?.totalVentaNeta) || 0;
    const impuestos = parseFloat(doc.total_impuestos_fe || doc.total_impuesto || doc.totales?.totalImpuestosFe || doc.totales?.totalImpuesto) || 0;
    const total = parseFloat(doc.total_comprobante || doc.totales?.totalComprobante) || 0;
    switch (field) {
      case 'tipo': return TIPO_DOC[tipoDoc] || tipoDoc;
      case 'consecutivo': return doc.consecutivo || '';
      case 'fecha': {
        const d = new Date(doc.fecha_emision || doc.fechaEmision || 0);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
      }
      case 'cliente': return (doc.receptor_nombre || doc.receptorNombre || '').toLowerCase();
      case 'divisa': return doc.codigo_moneda || doc.codigoMoneda || 'CRC';
      case 'termino': return (doc.condicion_venta || doc.condicionVenta) === '02' ? 'Crédito' : 'Contado';
      case 'referencia': return doc.referencia_numero || doc.referenciaNumero || '';
      case 'subtotal': return signo * subtotal * tc;
      case 'impuestos': return signo * impuestos * tc;
      case 'total': return signo * total * tc;
      case 'tc': return tc;
      case 'baseCRC': return parseFloat(doc.totalBaseImponibleColones) || 0;
      case 'ivaCRC': return parseFloat(doc.totalIvaColones) || 0;
      case 'tarifaIva': return subtotal > 0 ? impuestos / subtotal : 0;
      case 't911CRC': return parseFloat(doc.total911Colones) || 0;
      case 'cruzRojaCRC': return parseFloat(doc.totalCruzRojaColones) || 0;
      case 'cabys': return doc.cabysCodes || '';
      case 'estado': return STATUS_CONFIG[doc.status]?.label || doc.status || '';
      default: return '';
    }
  };

  // Sort documents. Empate → por consecutivo en la misma dirección.
  const sortedDocs = [...documentos].sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    const aVal = getSortValue(a, sortField);
    const bVal = getSortValue(b, sortField);
    let cmp = typeof aVal === 'number' && typeof bVal === 'number'
      ? aVal - bVal
      : String(aVal).localeCompare(String(bVal), 'es', { numeric: true, sensitivity: 'base' });
    if (cmp === 0 && sortField !== 'consecutivo') {
      cmp = String(a.consecutivo || '').localeCompare(String(b.consecutivo || ''));
    }
    return cmp * dir;
  });

  const formatCurrency = (amount, currency = 'CRC') => {
    if (!amount && amount !== 0) return '-';
    const symbol = currency === 'USD' ? '$' : '₡';
    const formatted = new Intl.NumberFormat('es-CR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return `${symbol}${formatted}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-CR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 inline ml-1 text-gray-300 opacity-0 group-hover:opacity-100" />;
    return sortDirection === 'asc' ?
      <ChevronUp className="h-3 w-3 inline ml-1" /> :
      <ChevronDown className="h-3 w-3 inline ml-1" />;
  };

  // Calculate totals grouped by currency
  const calculateTotals = () => {
    const totals = {};

    sortedDocs.forEach(doc => {
      const moneda = doc.codigo_moneda || doc.codigoMoneda || 'CRC';
      const tipoDoc = doc.tipo_documento || doc.tipoDocumento || '01';
      const isNC = tipoDoc === '03'; // Nota de crédito
      const isAnulado = doc.anuladoPorNC || doc.anulado_por_nc || doc.estado_pago === 'anulado' || doc.estadoPago === 'anulado';
      const isCancelled = doc.status === 'cancelled';

      if (!totals[moneda]) {
        totals[moneda] = {
          subtotal: 0, impuestos: 0, total: 0, count: 0,
          baseCRC: 0, ivaCRC: 0, t911CRC: 0, cruzRojaCRC: 0
        };
      }

      totals[moneda].count += 1;

      // Excluir del total:
      // - Anuladas con NC (la NC ya provee el offset)
      // - Canceladas (anuladas administrativamente)
      // - Rechazadas por Hacienda (no son válidas)
      // - Con error (no fueron procesadas)
      // - Pendientes/Procesando (aún no confirmadas)
      const invalidStatuses = ['cancelled', 'rejected', 'error', 'pending', 'processing'];
      if (isAnulado || invalidStatuses.includes(doc.status)) {
        return;
      }

      // NC: se restan (son el offset de otra factura)
      const multiplier = isNC ? -1 : 1;

      const subtotal = (doc.total_venta_neta || doc.totales?.totalVentaNeta || 0) * multiplier;
      const impuestos = (doc.total_impuestos_fe || doc.total_impuesto || doc.totales?.totalImpuestosFe || doc.totales?.totalImpuesto || 0) * multiplier;
      const total = (doc.total_comprobante || doc.totales?.totalComprobante || 0) * multiplier;
      const baseCRC = (parseFloat(doc.totalBaseImponibleColones) || 0) * multiplier;
      const ivaCRC = (parseFloat(doc.totalIvaColones) || 0) * multiplier;
      const t911CRC = (parseFloat(doc.total911Colones) || 0) * multiplier;
      const cruzRojaCRC = (parseFloat(doc.totalCruzRojaColones) || 0) * multiplier;

      totals[moneda].subtotal += subtotal;
      totals[moneda].impuestos += impuestos;
      totals[moneda].total += total;
      totals[moneda].baseCRC += baseCRC;
      totals[moneda].ivaCRC += ivaCRC;
      totals[moneda].t911CRC += t911CRC;
      totals[moneda].cruzRojaCRC += cruzRojaCRC;
    });

    return totals;
  };

  const totalesPorMoneda = calculateTotals();

  // Selection handler
  const handleSelectDoc = (doc) => {
    if (selectedDoc?.id === doc.id) {
      setSelectedDoc(null);
    } else {
      setSelectedDoc(doc);
    }
  };

  // Action handlers
  const handlePrint = () => {
    if (!selectedDoc) return;
    setShowPrintPreview(true);
  };

  const handleSendEmail = () => {
    if (!selectedDoc) return;
    if (selectedDoc.status !== 'accepted') {
      toast.error('Solo se pueden enviar por email documentos aceptados');
      return;
    }
    // Abrir modal de confirmación
    setShowEmailModal(true);
  };

  const handleConfirmSendEmail = async () => {
    if (!selectedDoc) return;

    setSendingEmail(true);
    try {
      await documentsApi.sendEmail(selectedDoc.id);
      toast.success('Email enviado al cliente');
      setShowEmailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCreateCreditNote = (tipo = 'anulacion') => {
    if (!selectedDoc) return;
    const tipoDoc = selectedDoc.tipo_documento || selectedDoc.tipoDocumento;
    if (selectedDoc.status !== 'accepted') {
      toast.error('Solo se pueden anular documentos aceptados');
      return;
    }
    if (tipoDoc === '03') {
      toast.error('No se puede crear NC de otra NC');
      return;
    }

    // Open NC modal
    setNCMotivo(tipo);
    setNCRazon(tipo === 'anulacion'
      ? 'Anulación total del documento'
      : tipo === 'descuento'
        ? 'Aplicación de descuento'
        : 'Devolución parcial');
    setShowNCModal(true);
  };

  // Process NC creation
  const handleConfirmNC = async (enviarHacienda = false) => {
    if (!selectedDoc) return;

    setCreatingNC(true);
    try {
      // Get full document data
      const docResponse = await documentsApi.getById(selectedDoc.id);
      const docData = docResponse.data?.data;

      if (!docData) {
        toast.error('Error al cargar datos del documento');
        return;
      }

      // Prepare NC data
      // IMPORTANTE: El tipo de cambio debe ser EXACTAMENTE el mismo que el documento original
      // Hacienda rechaza NC con tipo de cambio diferente al documento de referencia
      const tipoCambioOriginal = parseFloat(docData.tipoCambio) || parseFloat(docData.tipo_cambio) || 1;

      const ncData = {
        tipoDocumento: '03', // Nota de Crédito
        receptorId: docData.receptorId || null,
        condicionVenta: docData.condicionVenta || '01',
        medioPago: docData.medioPago || ['01'],
        codigoMoneda: docData.codigoMoneda || 'USD',
        tipoCambio: tipoCambioOriginal,
        notas: ncRazon,
        // Reference to original document
        referencia: {
          tipoDoc: docData.tipoDocumento || '01',
          numero: docData.clave, // La clave de 50 dígitos para Hacienda
          consecutivo: docData.consecutivo, // Consecutivo electrónico para contabilidad
          fecha: docData.fechaEmision ? docData.fechaEmision.split('T')[0] : new Date().toISOString().split('T')[0],
          codigo: ncMotivo === 'anulacion' ? '01' : ncMotivo === 'descuento' ? '02' : '03',
          razon: ncRazon
        },
        // Copy lines from original document for full cancellation
        // IMPORTANTE: Copiar también la exoneración si existe
        lineas: ncMotivo === 'anulacion' ? copiarLineasParaAnular(docData) : []
      };

      // Create NC
      const response = await documentsApi.create(ncData, enviarHacienda);

      if (enviarHacienda) {
        toast.success('Nota de Crédito creada y enviada a Hacienda');
      } else {
        toast.success('Nota de Crédito guardada como borrador');
      }

      // Close modal and reload
      setShowNCModal(false);
      setSelectedDoc(null);
      cargarDocumentos(pagination.page);

      // Navigate to NC list
      navigate('/facturacion/notas-credito');

    } catch (error) {
      console.error('Error creating NC:', error);
      toast.error(error.response?.data?.error || 'Error al crear Nota de Crédito');
    } finally {
      setCreatingNC(false);
    }
  };

  const handleCopy = async () => {
    if (!selectedDoc) return;

    try {
      const response = await documentsApi.copy(selectedDoc.id);
      if (response.data?.success) {
        toast.success('Documento copiado en Recuperar Facturas');
        // Stay on the same page - user can copy more documents
        setSelectedDoc(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al copiar documento');
    }
  };

  const handleViewDetails = () => {
    if (!selectedDoc) return;
    setShowDetailModal(true);
  };

  const handleDownloadXml = async () => {
    if (!selectedDoc) return;

    try {
      const response = await documentsApi.downloadXml(selectedDoc.id);

      // Check content-type to determine if it's a ZIP or XML
      const contentType = response.headers['content-type'] || '';
      const isZip = contentType.includes('application/zip');

      // Create blob with appropriate type
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isZip
        ? `${selectedDoc.consecutivo}.zip`
        : `${selectedDoc.consecutivo}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Show appropriate message
      if (isZip) {
        toast.success('Descargado: XML(s) + PDF del documento');
      } else {
        toast.success('XML descargado');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al descargar XML');
    }
  };

  // Delete cancelled document (only when consecutivo was reused by accepted doc)
  const handleDeleteCancelled = () => {
    if (!selectedDoc || !canDeleteSelected) return;
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDoc) return;
    setDeleting(true);
    try {
      await documentsApi.deleteCancelled(selectedDoc.id);
      toast.success('Documento cancelado eliminado');
      setShowDeleteModal(false);
      setSelectedDoc(null);
      cargarDocumentos(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar documento');
    } finally {
      setDeleting(false);
    }
  };

  // Abrir modal: valida que el doc sea convertible (aceptado, no NC, con cliente)
  const handleOpenRecurringModal = () => {
    if (!selectedDoc) return;
    const tipoDoc = selectedDoc.tipo_documento || selectedDoc.tipoDocumento;
    const estado = selectedDoc.estado_hacienda || selectedDoc.estadoHacienda;
    if (tipoDoc === '03') {
      toast.error('No se puede hacer recurrente una Nota de Crédito');
      return;
    }
    if (estado !== 'aceptado') {
      toast.error(`Solo se pueden convertir FE aceptadas por Hacienda. Estado actual: ${estado || 'desconocido'}`);
      return;
    }
    if (!selectedDoc.receptor_id && !selectedDoc.receptorId) {
      toast.error('Se requiere un cliente para hacer recurrente');
      return;
    }
    setShowRecurringModal(true);
  };

  // Confirmar desde RecurrenteConfigModal — llama al endpoint que usa createFromDocumento
  const handleConfirmRecurring = async (config) => {
    if (!selectedDoc) return;
    try {
      const res = await recurringApi.createFromDocumento(selectedDoc.id, config);
      toast.success(res.data?.message || 'Plantilla recurrente creada');
      setShowRecurringModal(false);
      setSelectedDoc(null);
      navigate('/facturacion/recurrente');
    } catch (error) {
      console.error('Error creating recurring:', error);
      toast.error(error.response?.data?.error || 'Error al crear plantilla recurrente');
    }
  };

  return (
    <div className="w-full flex flex-col h-[var(--alto-contenido)]">
      {/* Action Toolbar - always visible, buttons disabled when no selection */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 shrink-0">
        <div className="flex items-center justify-center gap-1 overflow-x-auto">
          {/* Imprimir */}
          <button
            onClick={handlePrint}
            disabled={!selectedDoc}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-slate-400 text-slate-600 hover:bg-slate-50 hover:border-slate-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <Printer className="h-5 w-5 mb-1" />
            <span>Imprimir</span>
          </button>

          {/* Enviar por Email */}
          <button
            onClick={handleSendEmail}
            disabled={!selectedDoc || sendingEmail || selectedDoc?.status !== 'accepted'}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <Mail className="h-5 w-5 mb-1" />
            <span>{sendingEmail ? 'Enviando...' : 'Email'}</span>
          </button>

          <div className="w-px h-12 bg-gray-300 mx-1" />

          {/* Anular con NC — bloqueado si ya está anulada (estado_pago='anulado' viene
              de la NC anuladora aceptada en statusChecker/documentService). No tiene
              sentido emitir otra NC para anular algo que ya fue anulado. */}
          {(() => {
            const tipoDoc = selectedDoc?.tipo_documento || selectedDoc?.tipoDocumento;
            const yaAnulada = selectedDoc?.estado_pago === 'anulado' || selectedDoc?.estadoPago === 'anulado';
            // NC anuladora ya enviada, esperando a Hacienda: tampoco se puede emitir otra
            const enTramite = selectedDoc?.ncAnulacionEnTramite;
            // NC solo sobre FE/TE: no sobre otra NC ni sobre una ND
            const disabled = !selectedDoc || selectedDoc?.status !== 'accepted' || tipoDoc === '03' || tipoDoc === '02' || yaAnulada || !!enTramite;
            const msgTramite = enTramite ? `La NC ${enTramite.consecutivo || ''} que anula esta FE está esperando respuesta de Hacienda` : '';
            const titleAnular = yaAnulada ? 'Esta FE ya fue anulada con una NC' : msgTramite;
            const titleDesc = yaAnulada ? 'Esta FE ya fue anulada — no puede emitir NC de descuento' : msgTramite;
            return (
              <>
                <button
                  onClick={() => handleCreateCreditNote('anulacion')}
                  disabled={disabled}
                  title={titleAnular}
                  className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-red-400 text-red-600 hover:bg-red-50 hover:border-red-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <XCircle className="h-5 w-5 mb-1" />
                  <span>Anular NC</span>
                </button>

                <button
                  onClick={() => handleCreateCreditNote('descuento')}
                  disabled={disabled}
                  title={titleDesc}
                  className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-amber-400 text-amber-600 hover:bg-amber-50 hover:border-amber-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <Percent className="h-5 w-5 mb-1" />
                  <span>NC Desc.</span>
                </button>
              </>
            );
          })()}

          <div className="w-px h-12 bg-gray-300 mx-1" />

          {/* Copiar */}
          <button
            onClick={handleCopy}
            disabled={!selectedDoc}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-indigo-400 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <Copy className="h-5 w-5 mb-1" />
            <span>Copiar</span>
          </button>

          {/* Descargar XML */}
          <button
            onClick={handleDownloadXml}
            disabled={!selectedDoc}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-teal-400 text-teal-600 hover:bg-teal-50 hover:border-teal-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <Download className="h-5 w-5 mb-1" />
            <span>XML</span>
          </button>

          <div className="w-px h-12 bg-gray-300 mx-1" />

          {/* Hacer Recurrente */}
          <button
            onClick={handleOpenRecurringModal}
            disabled={!selectedDoc || (selectedDoc?.tipo_documento || selectedDoc?.tipoDocumento) === '03'}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-cyan-400 text-cyan-600 hover:bg-cyan-50 hover:border-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <Repeat className="h-5 w-5 mb-1" />
            <span>Recurrente</span>
          </button>

          {/* Ver Detalles */}
          <button
            onClick={handleViewDetails}
            disabled={!selectedDoc}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-purple-400 text-purple-600 hover:bg-purple-50 hover:border-purple-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <Eye className="h-5 w-5 mb-1" />
            <span>Detalles</span>
          </button>

          <div className="w-px h-12 bg-gray-300 mx-1" />

          {/* Exportar Excel */}
          <button
            onClick={exportarExcel}
            disabled={exporting || documentos.length === 0}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-green-400 text-green-600 hover:bg-green-50 hover:border-green-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            {exporting ? <RefreshCw className="h-5 w-5 mb-1 animate-spin" /> : <FileSpreadsheet className="h-5 w-5 mb-1" />}
            <span>Excel</span>
          </button>

          {/* Exportar PDF */}
          <button
            onClick={exportarPdf}
            disabled={exporting || documentos.length === 0}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-red-400 text-red-600 hover:bg-red-50 hover:border-red-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            {exporting ? <RefreshCw className="h-5 w-5 mb-1 animate-spin" /> : <FileText className="h-5 w-5 mb-1" />}
            <span>PDF</span>
          </button>

          {/* Eliminar Cancelado - solo visible si se puede eliminar */}
          {canDeleteSelected && (
            <>
              <div className="w-px h-12 bg-gray-300 mx-1" />
              <button
                onClick={handleDeleteCancelled}
                disabled={deleting}
                className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-red-400 text-red-600 hover:bg-red-50 hover:border-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="h-5 w-5 mb-1" />
                <span>{deleting ? 'Eliminando...' : 'Eliminar'}</span>
              </button>
            </>
          )}

        </div>

        {/* Selected document info */}
        {selectedDoc && (
          <div className="mt-2 pt-2 border-t border-gray-100 text-sm text-gray-500 flex items-center justify-between">
            <div>
              Seleccionado: <span className="font-medium text-gray-900">{selectedDoc.consecutivo}</span>
              {(selectedDoc.receptor_nombre || selectedDoc.receptorNombre) && (
                <span className="ml-2">- {selectedDoc.receptor_nombre || selectedDoc.receptorNombre}</span>
              )}
            </div>
            <button
              onClick={() => setSelectedDoc(null)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 shrink-0">
        <div className="flex flex-col gap-3">
          {/* Primera fila: búsqueda y filtros principales */}
          <div className="flex flex-col md:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por consecutivo, clave, cliente o total..."
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </form>
            <div className="flex flex-wrap gap-2 items-center">
              {/* Filtro de período unificado — mes (◀ ▶) o rango personalizado via popup */}
              <MonthRangePicker
                value={filtroFecha}
                onChange={setFiltroFecha}
                onRefresh={() => cargarDocumentos(1)}
              />

              <select
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los tipos</option>
                <option value="01">Facturas (FE)</option>
                <option value="04">Tiquetes (TE)</option>
              </select>
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los estados</option>
                <option value="pending">Pendientes</option>
                <option value="processing">Procesando</option>
                <option value="accepted">Aceptadas</option>
                <option value="rejected">Rechazadas</option>
                <option value="error">Con Error</option>
                <option value="cancelled">Cancelados</option>
              </select>
              <button
                onClick={() => cargarDocumentos(1)}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                title="Recargar"
              >
                <RefreshCw className="h-4 w-4 text-gray-600" />
              </button>

              <label
                className="flex items-center gap-1.5 text-sm cursor-pointer select-none"
                title={autoRefresh
                  ? (hasProcessingDocs ? 'Auto-check activo (cada 30s)' : 'Auto-check habilitado (sin documentos procesando)')
                  : 'Auto-check desactivado'
                }
              >
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className={`flex items-center gap-1 ${autoRefresh && hasProcessingDocs ? 'text-green-600 font-medium' : 'text-gray-600'}`}>
                  Auto
                  {autoRefresh && hasProcessingDocs && (
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  )}
                </span>
              </label>

              {/* Column Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowColumnSelector(!showColumnSelector)}
                  className={clsx(
                    "p-2 border rounded-lg hover:bg-gray-50",
                    showColumnSelector ? "border-blue-500 bg-blue-50" : "border-gray-300"
                  )}
                  title="Configurar columnas"
                >
                  <Settings className="h-4 w-4 text-gray-600" />
                </button>
                {showColumnSelector && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowColumnSelector(false)} />
                    <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px]">
                      <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-3">
                        <span className="font-medium text-sm text-gray-700">Columnas</span>
                        <button
                          onClick={resetColumns}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                          title="Volver al orden y columnas por defecto"
                        >
                          <RotateCcw className="h-3 w-3" /> Restablecer
                        </button>
                      </div>
                      <div className="px-3 pt-1.5 text-[11px] text-gray-400">Arrastre los encabezados para reordenar</div>
                      <div className="py-1 max-h-64 overflow-y-auto">
                        {columnOrder.map(key => [key, COLUMN_CONFIG[key]]).map(([key, config]) => (
                          <button
                            key={key}
                            onClick={() => toggleColumn(key)}
                            disabled={config.fixed}
                            className={clsx(
                              "w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-gray-50",
                              config.fixed && "opacity-50 cursor-not-allowed"
                            )}
                          >
                            <span className={clsx(
                              "w-4 h-4 rounded border flex items-center justify-center",
                              visibleColumns[key] ? "bg-blue-600 border-blue-600" : "border-gray-300"
                            )}>
                              {visibleColumns[key] && <Check className="h-3 w-3 text-white" />}
                            </span>
                            <span>{config.shortLabel || config.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Rango personalizado ahora vive dentro del popup del MonthRangePicker */}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 min-h-0 flex flex-col">
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-2 text-gray-500">Cargando...</p>
          </div>
        ) : documentos.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No se encontraron comprobantes</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_rgba(229,231,235,1)]">
                <tr className="bg-gray-50 border-b border-gray-200">
                  {orderedColumns.map(colKey => {
                    const col = COLUMN_CONFIG[colKey];
                    return (
                      <th
                        key={colKey}
                        draggable
                        onDragStart={(e) => handleDragStart(e, colKey)}
                        onDragOver={(e) => handleDragOver(e, colKey)}
                        onDragEnd={handleDragEnd}
                        onClick={() => handleSort(colKey)}
                        title="Click para ordenar · arrastre para mover la columna"
                        className={clsx(
                          ALIGN_CLASS[col.align],
                          'group py-2 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100',
                          col.secondary ? 'px-2 font-medium text-gray-500 text-xs' : 'px-3 font-medium text-gray-700',
                          sortField === colKey && 'bg-blue-50 text-blue-700',
                          draggedColumn === colKey && 'bg-blue-100 opacity-60'
                        )}
                      >
                        <GripVertical className="h-3 w-3 inline mr-0.5 -ml-1 text-gray-300 opacity-0 group-hover:opacity-100 cursor-move" />
                        {col.label}
                        <SortIcon field={colKey} />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedDocs.map((doc) => {
                  const StatusIcon = STATUS_CONFIG[doc.status]?.icon || Clock;
                  const tipoDoc = doc.tipo_documento || doc.tipoDocumento || '01';
                  const isNC = tipoDoc === '03';
                  const isND = tipoDoc === '02';
                  const isAnulado = doc.anuladoPorNC || doc.anulado_por_nc;
                  const isCancelled = doc.status === 'cancelled';

                  // Get totals and currency (handle different naming conventions)
                  // Las anuladas muestran valor original (tachado) - la NC provee el offset
                  // Las canceladas muestran valor original (tachado) - se excluyen de totales
                  const subtotal = doc.total_venta_neta || doc.totales?.totalVentaNeta || 0;
                  const impuestos = doc.total_impuestos_fe || doc.total_impuesto || doc.totales?.totalImpuestosFe || doc.totales?.totalImpuesto || 0;
                  const total = doc.total_comprobante || doc.totales?.totalComprobante || 0;
                  const moneda = doc.codigo_moneda || doc.codigoMoneda || 'CRC';

                  // Valores CRC
                  const baseCRC = parseFloat(doc.totalBaseImponibleColones) || 0;
                  const ivaCRC = parseFloat(doc.totalIvaColones) || 0;
                  const t911CRC = parseFloat(doc.total911Colones) || 0;
                  const cruzRojaCRC = parseFloat(doc.totalCruzRojaColones) || 0;
                  const tipoCambio = parseFloat(doc.tipoCambio) || 1;

                  const rowCells = {
                    tipo: (
                      <td className="px-3 py-2">
                        <span
                          className={clsx(
                            'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                            isCancelled ? 'bg-gray-200 text-gray-500' :
                            isAnulado ? 'bg-red-100 text-red-700' :
                            isNC ? 'bg-orange-100 text-orange-700' :
                            isND ? 'bg-purple-100 text-purple-700' :
                            'bg-blue-100 text-blue-700'
                          )}
                          title={TIPO_DOC_FULL[tipoDoc]}
                        >
                          {TIPO_DOC[tipoDoc] || tipoDoc}
                        </span>
                      </td>
                    ),
                    consecutivo: (
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className={clsx(
                            "font-mono text-xs",
                            isAnulado ? "text-gray-400 line-through decoration-red-400" :
                            isCancelled ? "text-gray-400 line-through decoration-gray-400" : "text-gray-900"
                          )}>
                            {doc.consecutivo}
                          </span>
                          {isCancelled && (
                            <span className="text-[9px] px-1 py-px bg-gray-300 text-gray-600 rounded font-medium">
                              CANCELADO
                            </span>
                          )}
                          {!isAnulado && doc.ncAnulacionEnTramite && (
                            <span
                              className="text-[9px] px-1 py-px bg-amber-100 text-amber-700 rounded font-medium whitespace-nowrap"
                              title={`NC ${doc.ncAnulacionEnTramite.consecutivo || ''} esperando respuesta de Hacienda`}
                            >
                              ANULACIÓN EN TRÁMITE
                            </span>
                          )}
                        </div>
                      </td>
                    ),
                    fecha: (
                      <td className="px-2 py-2 text-gray-600 whitespace-nowrap text-sm">
                        {formatDate(doc.fecha_emision || doc.fechaEmision)}
                      </td>
                    ),
                    cliente: (
                      <td className="px-3 py-2">
                        <div className={clsx(
                          "font-medium",
                          isAnulado ? "text-gray-400 line-through decoration-red-400" : "text-gray-900"
                        )}>
                          {doc.receptor_nombre || doc.receptorNombre || 'Sin cliente'}
                        </div>
                      </td>
                    ),
                    divisa: (
                      <td className="px-2 py-2 text-center">
                        <span className={clsx(
                          "text-xs font-medium",
                          isAnulado ? "text-gray-400" : moneda === 'USD' ? "text-blue-600" : "text-gray-600"
                        )}>
                          {moneda}
                        </span>
                      </td>
                    ),
                    termino: (
                      <td className="px-2 py-2 text-center">
                        <span className={clsx(
                          "text-xs",
                          isAnulado ? "text-gray-400" :
                          (doc.condicion_venta || doc.condicionVenta) === '02' ? "text-amber-600" : "text-gray-600"
                        )}>
                          {(doc.condicion_venta || doc.condicionVenta) === '02' ? 'Crédito' : 'Contado'}
                        </span>
                      </td>
                    ),
                    referencia: (
                      <td className="px-3 py-2">
                        {(doc.referencia_numero || doc.referenciaNumero) ? (
                          <span className="text-xs text-gray-600 font-mono">
                            {doc.referencia_numero || doc.referenciaNumero}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    ),
                    subtotal: (
                      <td className={clsx(
                        "px-3 py-2 text-right text-sm",
                        isAnulado ? "text-gray-400 line-through decoration-red-400" : "text-gray-700"
                      )}>
                        {formatCurrency(subtotal, moneda)}
                      </td>
                    ),
                    impuestos: (
                      <td className={clsx(
                        "px-3 py-2 text-right text-sm",
                        isAnulado ? "text-gray-400 line-through decoration-red-400" : "text-gray-700"
                      )}>
                        {formatCurrency(impuestos, moneda)}
                      </td>
                    ),
                    total: (
                      <td className={clsx(
                        'px-3 py-2 text-right text-sm font-bold',
                        isAnulado ? 'text-gray-400 line-through decoration-red-400' :
                        isNC ? 'text-orange-600' : 'text-gray-900'
                      )}>
                        {isNC && '-'}{formatCurrency(total, moneda)}
                      </td>
                    ),
                    tc: (
                      <td className={clsx(
                        "px-2 py-2 text-center text-sm",
                        (isAnulado || isCancelled) ? "text-gray-400" : "text-gray-500"
                      )}>
                        {tipoCambio && tipoCambio !== 1
                          ? `₡${tipoCambio.toFixed(2)}`
                          : '-'}
                      </td>
                    ),
                    baseCRC: (
                      <td className={clsx(
                        "px-2 py-2 text-right text-sm",
                        (isAnulado || isCancelled) ? "text-gray-400 line-through decoration-red-400" : "text-gray-500"
                      )}>
                        {baseCRC !== 0
                          ? `₡${baseCRC.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`
                          : '-'}
                      </td>
                    ),
                    ivaCRC: (
                      <td className={clsx(
                        "px-2 py-2 text-right text-sm",
                        (isAnulado || isCancelled) ? "text-gray-400 line-through decoration-red-400" : "text-gray-500"
                      )}>
                        {`₡${ivaCRC.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`}
                      </td>
                    ),
                    tarifaIva: (
                      <td className={clsx(
                        "px-2 py-2 text-center text-sm",
                        (isAnulado || isCancelled) ? "text-gray-400" : "text-gray-500"
                      )}>
                        {subtotal > 0 && impuestos > 0
                          ? `${Math.round((impuestos / subtotal) * 100)}%`
                          : (impuestos === 0 ? '0%' : '-')}
                      </td>
                    ),
                    t911CRC: (
                      <td className={clsx(
                        "px-2 py-2 text-right text-sm",
                        (isAnulado || isCancelled) ? "text-gray-400 line-through decoration-red-400" : "text-gray-500"
                      )}>
                        {t911CRC !== 0
                          ? `₡${t911CRC.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`
                          : '-'}
                      </td>
                    ),
                    cruzRojaCRC: (
                      <td className={clsx(
                        "px-2 py-2 text-right text-sm",
                        (isAnulado || isCancelled) ? "text-gray-400 line-through decoration-red-400" : "text-gray-500"
                      )}>
                        {cruzRojaCRC !== 0
                          ? `₡${cruzRojaCRC.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`
                          : '-'}
                      </td>
                    ),
                    cabys: (
                      <td className="px-2 py-2 text-left text-xs font-mono text-gray-500 max-w-[150px] truncate" title={doc.cabysCodes || '-'}>
                        {doc.cabysCodes || '-'}
                      </td>
                    ),
                    estado: (
                      <td className="px-3 py-2 text-center">
                        {isCancelled ? (
                          <span className="text-gray-400">-</span>
                        ) : (
                          <span
                            className={clsx(
                              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                              STATUS_CONFIG[doc.status]?.color || 'bg-gray-100 text-gray-700',
                              (doc.status === 'error' || doc.status === 'rejected' || doc.status === 'pending') && 'cursor-pointer hover:ring-2 hover:ring-blue-300'
                            )}
                            onClick={() => {
                              if (doc.status === 'error' || doc.status === 'rejected' || doc.status === 'pending') {
                                setMensajeHaciendaModal({ doc, mensaje: doc.mensajeHacienda || (doc.status === 'pending' ? 'Este documento está pendiente de envío a Hacienda. Puede reenviarlo.' : '') });
                              }
                            }}
                            title={(doc.status === 'error' || doc.status === 'rejected' || doc.status === 'pending')
                              ? 'Click para ver opciones'
                              : undefined}
                          >
                            <StatusIcon className="h-3 w-3" />
                            {STATUS_CONFIG[doc.status]?.label || doc.status}
                          </span>
                        )}
                      </td>
                    ),
                  };
                  const isSelected = selectedDoc?.id === doc.id;
                  return (
                    <tr
                      key={doc.id}
                      onClick={() => handleSelectDoc(doc)}
                      className={clsx(
                        "cursor-pointer transition-colors",
                        isAnulado && "bg-red-50/60",
                        isCancelled && "bg-gray-100/80",
                        isSelected ? "bg-blue-50 hover:bg-blue-100" : isAnulado ? "hover:bg-red-100/80" : isCancelled ? "hover:bg-gray-200/80" : "hover:bg-gray-50"
                      )}
                    >
                      {orderedColumns.map(colKey => <Fragment key={colKey}>{rowCells[colKey]}</Fragment>)}
                    </tr>
                  );
                })}
              </tbody>
              {/* Totales por moneda — sticky al pie del scroll para verse siempre */}
              {Object.keys(totalesPorMoneda).length > 0 && (
                <tfoot className="sticky bottom-0 z-10 bg-gray-100 border-t-2 border-gray-400 shadow-[0_-1px_0_rgba(156,163,175,1)]">
                  {Object.entries(totalesPorMoneda).map(([moneda, totals], idx) => {
                    const footerCells = {
                      tipo: (
                        <td className="px-3 py-2 text-sm text-gray-600 font-medium whitespace-nowrap">
                          {idx === 0 && (pagination.total > documentos.length
                            ? `${documentos.length} de ${pagination.total} facturas`
                            : `${documentos.length} ${documentos.length === 1 ? 'factura' : 'facturas'}`)}
                        </td>
                      ),
                      consecutivo: (
                        <td className="px-3 py-2"></td>
                      ),
                      fecha: (
                        <td className="px-3 py-2"></td>
                      ),
                      cliente: (
                        <td className="px-3 py-2"></td>
                      ),
                      divisa: (
                        <td className="px-2 py-2"></td>
                      ),
                      termino: (
                        <td className="px-2 py-2"></td>
                      ),
                      referencia: (
                        <td className="px-3 py-2"></td>
                      ),
                      subtotal: (
                        <td className="px-3 py-2 text-right text-sm font-semibold text-gray-700">
                          {formatCurrency(totals.subtotal, moneda)}
                        </td>
                      ),
                      impuestos: (
                        <td className="px-3 py-2 text-right text-sm font-semibold text-gray-700">
                          {formatCurrency(totals.impuestos, moneda)}
                        </td>
                      ),
                      total: (
                        <td className="px-3 py-2 text-right text-sm font-bold text-gray-900">
                          {formatCurrency(totals.total, moneda)}
                        </td>
                      ),
                      tc: (
                        <td className="px-2 py-2"></td>
                      ),
                      baseCRC: (
                        <td className="px-2 py-2 text-right text-sm font-semibold text-gray-600">
                          ₡{totals.baseCRC.toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                        </td>
                      ),
                      ivaCRC: (
                        <td className="px-2 py-2 text-right text-sm font-semibold text-gray-600">
                          ₡{totals.ivaCRC.toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                        </td>
                      ),
                      tarifaIva: (
                        <td className="px-2 py-2"></td>
                      ),
                      t911CRC: (
                        <td className="px-2 py-2 text-right text-sm font-semibold text-gray-600">
                          ₡{totals.t911CRC.toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                        </td>
                      ),
                      cruzRojaCRC: (
                        <td className="px-2 py-2 text-right text-sm font-semibold text-gray-600">
                          ₡{totals.cruzRojaCRC.toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                        </td>
                      ),
                      cabys: (
                        <td className="px-2 py-2"></td>
                      ),
                      estado: (
                        <td className="px-3 py-2"></td>
                      ),
                    };
                    return (
                      <tr key={moneda}>
                        {orderedColumns.map(colKey => <Fragment key={colKey}>{footerCells[colKey]}</Fragment>)}
                      </tr>
                    );
                  })}
                </tfoot>
              )}
            </table>
          </div>
        )}

        {/* Paginación — solo aparece como fallback si el total supera el límite alto (>10000) */}
        {pagination.pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end bg-gray-50 shrink-0">
            <div className="flex gap-2">
              <button
                onClick={() => cargarDocumentos(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm bg-white disabled:opacity-50 hover:bg-gray-50"
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-sm text-gray-600">
                {pagination.page} / {pagination.pages}
              </span>
              <button
                onClick={() => cargarDocumentos(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded text-sm bg-white disabled:opacity-50 hover:bg-gray-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Print Preview Modal */}
      {showPrintPreview && selectedDoc && (
        <PrintPreviewModal
          documentId={selectedDoc.id}
          emisor={currentEmisor}
          onClose={() => setShowPrintPreview(false)}
        />
      )}

      {/* Document Detail Modal */}
      {showDetailModal && selectedDoc && (
        <DocumentDetailModal
          documentId={selectedDoc.id}
          emisor={currentEmisor}
          onClose={() => setShowDetailModal(false)}
        />
      )}

      {/* Modal Mensaje Hacienda (errores/rechazos) */}
      {mensajeHaciendaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setMensajeHaciendaModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b bg-red-50 rounded-t-xl">
              <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Respuesta de Hacienda
              </h3>
              <button onClick={() => setMensajeHaciendaModal(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              <div className="mb-3 text-sm text-gray-600">
                <strong>Consecutivo:</strong> {mensajeHaciendaModal.doc?.consecutivo}
              </div>
              <div className="mb-3 text-sm text-gray-600">
                <strong>Cliente:</strong> {mensajeHaciendaModal.doc?.receptor_nombre || mensajeHaciendaModal.doc?.receptorNombre || 'Sin cliente'}
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                <pre className="text-sm text-red-800 whitespace-pre-wrap font-mono select-text">
                  {mensajeHaciendaModal.mensaje}
                </pre>
              </div>
              <p className="mt-2 text-xs text-gray-500">Puedes seleccionar el texto con el mouse para copiarlo</p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(mensajeHaciendaModal.mensaje);
                  toast.success('Mensaje copiado');
                }}
                className="px-4 py-2 text-sm border border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50"
              >
                Copiar Mensaje
              </button>
              {(mensajeHaciendaModal.doc?.status === 'rejected' || mensajeHaciendaModal.doc?.status === 'pending') && (
                <>
                  <button
                    onClick={() => {
                      const docId = mensajeHaciendaModal.doc.id;
                      setMensajeHaciendaModal(null);
                      navigate(`/facturacion/nueva?cargar=${docId}&rejected=1`);
                    }}
                    className="px-4 py-2 text-sm border border-teal-500 text-teal-600 rounded-lg hover:bg-teal-50 flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Editar y Corregir
                  </button>
                  <button
                    onClick={async () => {
                      const docId = mensajeHaciendaModal.doc.id;
                      setMensajeHaciendaModal(null);
                      try {
                        toast.loading('Reenviando a Hacienda...', { id: 'reenvio' });
                        await documentsApi.submit(docId);
                        toast.success('Documento reenviado a Hacienda', { id: 'reenvio' });
                        cargarDocumentos();
                      } catch (error) {
                        toast.error(error.response?.data?.error || 'Error al reenviar', { id: 'reenvio' });
                      }
                    }}
                    className="px-4 py-2 text-sm border border-orange-500 text-orange-600 rounded-lg hover:bg-orange-50 flex items-center gap-2"
                  >
                    <Repeat className="h-4 w-4" />
                    Reenviar sin cambios
                  </button>
                  {/* Solo con -99: Hacienda ya tiene ese número registrado y no lo acepta otra vez */}
                  {mensajeHaciendaModal.doc?.status === 'rejected' && /-99\s*,/.test(mensajeHaciendaModal.mensaje || '') && (
                    <button
                      onClick={async () => {
                        const docId = mensajeHaciendaModal.doc.id;
                        setMensajeHaciendaModal(null);
                        try {
                          toast.loading('Reenviando con nuevo consecutivo...', { id: 'reenvio' });
                          const r = await documentsApi.submit(docId, { nuevoConsecutivo: true });
                          const nuevo = r.data?.data?.consecutivo;
                          toast.success(`Reenviado a Hacienda${nuevo ? ` con el consecutivo ${nuevo}` : ''}`, { id: 'reenvio' });
                          cargarDocumentos();
                        } catch (error) {
                          toast.error(error.response?.data?.error || 'Error al reenviar', { id: 'reenvio' });
                        }
                      }}
                      className="px-4 py-2 text-sm border border-indigo-500 text-indigo-600 rounded-lg hover:bg-indigo-50 flex items-center gap-2 whitespace-nowrap"
                    >
                      <Repeat className="h-4 w-4" />
                      Reenviar con nuevo consecutivo
                    </button>
                  )}
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="px-4 py-2 text-sm border border-red-500 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
                  >
                    <XCircle className="h-4 w-4" />
                    Cancelar Documento
                  </button>
                </>
              )}
              <button
                onClick={() => setMensajeHaciendaModal(null)}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NC Creation Modal */}
      {/* Modal vista-previa anular FE con NC — muestra FE original vs NC propuesta
          lado a lado, valida totales antes de permitir el envío. Reemplaza el modal
          simple anterior que emitía la NC "a ciegas" (causa del bug Fiberlink). */}
      {showNCModal && selectedDoc && (
        <AnulacionNCPreviewModal
          selectedDoc={selectedDoc}
          motivo={ncMotivo}
          razonInicial={ncRazon}
          onClose={() => setShowNCModal(false)}
          onCreated={() => {
            cargarDocumentos(pagination.page);
            setSelectedDoc(null);
            navigate('/facturacion/notas-credito');
          }}
        />
      )}


      {/* Modal de Plantilla Recurrente (compartido con CrearFactura) */}
      {showRecurringModal && selectedDoc && (
        <RecurrenteConfigModal
          defaultNombre={selectedDoc.receptor_nombre || selectedDoc.receptorNombre || ''}
          onClose={() => setShowRecurringModal(false)}
          onConfirm={handleConfirmRecurring}
        />
      )}

      {/* Modal Confirmar Envío Email */}
      {showEmailModal && selectedDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => !sendingEmail && setShowEmailModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-4 border-b bg-blue-50 rounded-t-xl">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Enviar Documento por Email</h2>
                <p className="text-sm text-gray-600">Se enviará el comprobante electrónico al cliente</p>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Document info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-500 mb-1">Documento a enviar:</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono font-medium text-gray-900">{selectedDoc.consecutivo}</p>
                    <p className="text-xs text-gray-500">
                      {TIPO_DOC_FULL[selectedDoc.tipo_documento || selectedDoc.tipoDocumento] || 'Documento'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      {(selectedDoc.codigo_moneda || selectedDoc.codigoMoneda) === 'USD' ? '$' : '₡'}
                      {(selectedDoc.total_comprobante || selectedDoc.totales?.totalComprobante || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(selectedDoc.fecha_emision || selectedDoc.fechaEmision)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Recipient info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-800 mb-2">Se enviará a:</p>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <Mail className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {selectedDoc.receptor_nombre || selectedDoc.receptorNombre || 'Cliente'}
                    </p>
                    <p className="text-sm text-blue-700">
                      {selectedDoc.receptor_email_facturacion || selectedDoc.receptorEmailFacturacion ||
                       selectedDoc.receptor_email || selectedDoc.receptorEmail || 'Sin email configurado'}
                    </p>
                  </div>
                </div>
              </div>

              {/* What will be sent */}
              <div className="text-sm text-gray-600">
                <p className="font-medium mb-1">El correo incluirá:</p>
                <ul className="list-disc list-inside space-y-0.5 text-gray-500">
                  <li>PDF del comprobante</li>
                  <li>XML firmado del documento</li>
                  <li>XML de respuesta de Hacienda</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowEmailModal(false)}
                disabled={sendingEmail}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmSendEmail}
                disabled={sendingEmail || !(selectedDoc.receptor_email_facturacion || selectedDoc.receptorEmailFacturacion || selectedDoc.receptor_email || selectedDoc.receptorEmail)}
                className="flex-1 px-4 py-2.5 border border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {sendingEmail ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Enviar Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cancelar Documento */}
      {showCancelModal && mensajeHaciendaModal?.doc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setShowCancelModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-4 border-b bg-red-50 rounded-t-xl">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Cancelar Documento</h2>
                <p className="text-sm text-gray-600">Esta acción no se puede deshacer</p>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-500 mb-1">Documento a cancelar:</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono font-medium text-gray-900">{mensajeHaciendaModal.doc.consecutivo}</p>
                    <p className="text-sm text-gray-600">{mensajeHaciendaModal.doc.receptor_nombre || mensajeHaciendaModal.doc.receptorNombre || 'Sin cliente'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      {(mensajeHaciendaModal.doc.codigo_moneda || mensajeHaciendaModal.doc.codigoMoneda) === 'USD' ? '$' : '₡'}
                      {(mensajeHaciendaModal.doc.total_comprobante || mensajeHaciendaModal.doc.totales?.totalComprobante || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">El documento será cancelado definitivamente:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                    <li>No se podrá enviar a Hacienda</li>
                    <li>El consecutivo será reciclado para uso futuro</li>
                    <li>El documento quedará en el historial como "Cancelado"</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={cancellingDoc}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Volver
              </button>
              <button
                onClick={async () => {
                  const doc = mensajeHaciendaModal.doc;
                  setCancellingDoc(true);
                  try {
                    await documentsApi.cancel(doc.id, doc.mensajeHacienda || 'Documento rechazado - cancelado por usuario');
                    toast.success('Documento cancelado. Consecutivo reciclado.');
                    setShowCancelModal(false);
                    setMensajeHaciendaModal(null);
                    cargarDocumentos();
                  } catch (error) {
                    toast.error(error.response?.data?.error || 'Error al cancelar');
                  } finally {
                    setCancellingDoc(false);
                  }
                }}
                disabled={cancellingDoc}
                className="flex-1 px-4 py-2.5 border border-red-500 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {cancellingDoc ? (
                  <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                Sí, Cancelar Documento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Eliminar Documento Cancelado */}
      {showDeleteModal && selectedDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => !deleting && setShowDeleteModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-4 border-b bg-red-50 rounded-t-xl">
              <div className="p-2 bg-red-100 rounded-lg">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-800">Eliminar Documento Cancelado</h3>
                <p className="text-sm text-red-600">Esta acción no se puede deshacer</p>
              </div>
            </div>

            <div className="p-5">
              <div className="bg-gray-50 rounded-lg p-4 border">
                <p className="text-sm text-gray-500 mb-2">Documento a eliminar:</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono font-medium text-gray-900">{selectedDoc.consecutivo}</p>
                    <p className="text-sm text-gray-600">{selectedDoc.receptor_nombre || selectedDoc.receptorNombre || 'Sin cliente'}</p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-700">
                      CANCELADO
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> Este documento fue cancelado y su consecutivo ya fue reutilizado por otro documento aceptado. Es seguro eliminarlo.
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 border border-red-500 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Sí, Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
